import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import apiService from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import {
  Modal,
  Button,
  Alert,
  LoadingOverlay,
  Box,
  TextInput,
  Text,
  Stack,
  Tabs,
  Tooltip,
  Group,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconArrowRight, IconPackage, IconInfoCircle, IconUserCircle, IconRepeat } from '@tabler/icons-react';
import { localizedField } from '../utils/localizedField';
import { effectiveCustomerPrice } from '../utils/adminCustomerPrice';
import { computeAdminOrderTotals, getShippingCostForSubtotal, todayIso, daysBetweenIso } from '../utils/adminOrderTotals';
import { matchOngoingOrder } from '../utils/runningOrder';
import { addressLabel } from '../utils/addressLabel';
import { runningOrderLines } from '../utils/runningOrderLine';
import OrderItemsTab from './takeOrder/OrderItemsTab';
import OrderPaymentTab from './takeOrder/OrderPaymentTab';
import OrderAddressTab from './takeOrder/OrderAddressTab';
import OrderTotalsSummary from './takeOrder/OrderTotalsSummary';
import OffersPanel from './takeOrder/OffersPanel';
import CustomerPricingPanel from './takeOrder/CustomerPricingPanel';
import './takeOrder/takeOrder.css';

const TABS = ['items', 'payment', 'address'];

let localKeyCounter = 0;
function localKey() {
  localKeyCounter += 1;
  return `to-${Date.now()}-${localKeyCounter}`;
}

function newProductItem() {
  return { localId: localKey(), type: 'product', variant_id: '', name: '', sku: '', quantity: 1, unit: 'pcs', unit_price: '', vat_rate: 8.1, discount_percent: 0, priceAuto: false };
}

export default function AdminCreateOrderModal({ user, onClose, onSuccess, onShowOngoing, loadOngoingOrderId }) {
  const { t } = useTranslation();
  const lang = t.langCode || 'en';

  // ── Data loading ────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [editingRunningOrder, setEditingRunningOrder] = useState(false);
  const [products, setProducts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [config, setConfig] = useState({ units: ['pcs'], vatRates: null, shippingTiers: [], defaultDueDateDays: 14 });
  const [customerPrices, setCustomerPrices] = useState([]);
  // Running monthly orders this customer already has open, so a new monthly order
  // can be presented as "add to the running order" instead of silently creating
  // a second order that would bill separately.
  const [ongoingOrders, setOngoingOrders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiService.getAdminProducts({ lang }).catch((err) => { console.error(err); return []; }),
      apiService.getAdminUserAddresses(user.id).catch((err) => { console.error(err); return []; }),
      apiService.getAdminUserBillingAddresses(user.id).catch((err) => { console.error(err); return []; }),
      apiService.getAdminUserCustomerPrices(user.id).catch((err) => { console.error(err); return []; }),
      apiService.getPublicPickupLocations().catch((err) => { console.error(err); return []; }),
      apiService.getUpcomingHolidays().catch((err) => { console.error(err); return []; }),
      apiService.getAdminOrderConfig().catch((err) => { console.error(err); return {}; }),
      apiService.getOngoingOrders(user.id).catch((err) => { console.error(err); return { ongoingOrders: [] }; }),
    ]).then(([prods, addrs, billAddrs, custPrices, locs, hols, cfg, ongoing]) => {
      if (cancelled) return;
      setProducts(Array.isArray(prods) ? prods : []);
      setAddresses(Array.isArray(addrs) ? addrs : []);
      setBillingAddresses(Array.isArray(billAddrs) ? billAddrs : []);
      setLocations(Array.isArray(locs) ? locs : []);
      setHolidays(Array.isArray(hols) ? hols : []);
      setCustomerPrices(Array.isArray(custPrices) ? custPrices : []);
      setOngoingOrders(Array.isArray(ongoing?.ongoingOrders) ? ongoing.ongoingOrders : []);
      setConfig({
        units: cfg.units && cfg.units.length ? cfg.units : ['pcs', 'g', 'kg', 'ml', 'L'],
        vatRates: cfg.vatRates || null,
        shippingTiers: cfg.shippingTiers || [],
        defaultDueDateDays: cfg.defaultDueDateDays || 14,
      });
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, user.id]);

  // ── Wizard state ────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [tab, setTab] = useState('items');

  const [items, setItems] = useState([newProductItem()]);
  const [description, setDescription] = useState('');
  const [orderDate, setOrderDate] = useState(todayIso());
  const [paymentMethod, setPaymentMethod] = useState(user.monthly_invoicing_enabled ? 'monthly' : 'invoice');
  const [dueDays, setDueDays] = useState('');

  const [fulfillmentMethod, setFulfillmentMethod] = useState('delivery');
  const [shippingOverride, setShippingOverride] = useState('');
  // Order-wide discount in percent. Empty = not set: for a new order that means no
  // discount, for a batch appended to a running order it keeps the standing value.
  const [cartDiscountPercent, setCartDiscountPercent] = useState('');
  const [shippingAddressId, setShippingAddressId] = useState('');
  const [billingAddressId, setBillingAddressId] = useState('');
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDate, setPickupDate] = useState('');

  // Where this batch is destined. Pickup orders persist no shipping address, and the
  // running-order identity keys on this exact value — so derive it ONCE here and
  // reuse it for both the submitted payload and the running-order lookup, rather
  // than letting the two expressions drift into disagreeing about which running
  // order the batch belongs to.
  const destinationShippingId = fulfillmentMethod === 'pickup' ? null : (shippingAddressId || null);

  // Auto-applied offers (evaluated server-side against the draft items).
  const [offerResult, setOfferResult] = useState(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState(null);
  const [applyVolumeDiscount, setApplyVolumeDiscount] = useState(null);
  // Offer ids from the last evaluation, used to tell a genuinely NEW applicable
  // offer apart from one the admin deliberately deselected (see evaluateOffers).
  const prevOfferIdsRef = useRef(null);
  // Offers the admin explicitly turned OFF. Unlike prevOfferIdsRef, this is
  // sticky across evaluations: an offer that drops out of the applicable set
  // (e.g. trigger quantity or subtotal dips) and later returns must NOT be
  // re-added — only an explicit admin re-toggle turns it back on.
  const deselectedOfferIdsRef = useRef(new Set());

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the day count with the store default (14) once it loads.
  useEffect(() => {
    setDueDays((prev) => (prev === '' || prev == null) && config.defaultDueDateDays ? config.defaultDueDateDays : prev);
  }, [config.defaultDueDateDays]);

  // Auto-select the user's default/first addresses once loaded.
  useEffect(() => {
    if (addresses.length > 0 && !shippingAddressId) {
      setShippingAddressId(addresses[0].id);
    }
  }, [addresses, shippingAddressId]);
  useEffect(() => {
    if (billingAddresses.length > 0 && !billingAddressId) {
      setBillingAddressId(billingAddresses[0].id);
    }
  }, [billingAddresses, billingAddressId]);

  // The running order this batch would be appended to. Keyed exactly as the server
  // keys it: (customer, billing address, destination) — so the banner can never
  // claim an append the backend would not perform, and a customer with two delivery
  // addresses keeps two independent running orders.
  const ongoingMatch = useMemo(() => {
    if (paymentMethod !== 'monthly') return null;
    return matchOngoingOrder(ongoingOrders, { billingAddressId, shippingAddressId: destinationShippingId });
  }, [ongoingOrders, paymentMethod, billingAddressId, destinationShippingId]);

  // Show the standing discount of the running order this batch would join, so the
  // admin sees what is applied and can change it. Only re-syncs when the target
  // running order changes, so it never overwrites what was just typed.
  const ongoingMatchId = ongoingMatch?.id || null;
  const lastOngoingMatchIdRef = useRef(null);
  useEffect(() => {
    if (lastOngoingMatchIdRef.current === ongoingMatchId) return;
    lastOngoingMatchIdRef.current = ongoingMatchId;
    if (ongoingMatch) setCartDiscountPercent(ongoingMatch.cart_discount_percent ?? '');
  }, [ongoingMatchId, ongoingMatch]);

  // Say which running order this batch belongs to. With one running order per
  // delivery destination a customer can have several open at once, so "monthly"
  // alone no longer tells the admin where the lines are going — and picking a
  // different destination silently targets a different order. Naming it here makes
  // that visible before saving.
  const runningOrderNotice = useMemo(() => {
    if (paymentMethod !== 'monthly' || step !== 2) return null;
    // A null destination IS the pickup case (see destinationShippingId), so read it
    // from there rather than from isPickup, which is declared further down.
    const destination = destinationShippingId
      ? addressLabel(addresses.find(a => a.id === destinationShippingId))
      : t('takeOrderDestinationPickup', 'pickup');
    if (ongoingMatch) {
      return ongoingMatch.item_count > 0
        ? t('takeOrderRunningAppend', 'This batch is added to the open running order for {destination} ({count} items already on it).', {
            destination, count: ongoingMatch.item_count,
          })
        : t('takeOrderRunningAppendEmpty', 'This batch is added to the open running order for {destination}.', { destination });
    }
    return t('takeOrderRunningCreate', 'No running order is open for {destination} yet, so this batch starts one.', { destination });
  }, [paymentMethod, step, addresses, destinationShippingId, ongoingMatch, t]);

  // SINGLE OWNER for turning a stored running order back into form state. Both load
  // paths (the loadOngoingOrderId effect and the "Add to Running Order" shortcut) get
  // the same shape from getOngoingOrder and must land on identical state — inline
  // copies drifted, which is how dueDays and the pickup fields came to be silently
  // dropped on load.
  const hydrateFromOrder = useCallback((order) => {
    setBillingAddressId(order.billing_address_id || '');
    setShippingAddressId(order.shipping_address_id || '');
    setFulfillmentMethod(order.shipping_address_id ? 'delivery' : 'pickup');
    setDescription(order.description || '');
    const loadedOrderDate = order.order_date ? String(order.order_date).slice(0, 10) : todayIso();
    setOrderDate(loadedOrderDate);
    setPaymentMethod(order.payment_method || 'monthly');
    // due_days is transient: only orders.due_date is persisted, so the day count has
    // to be recovered from it. A missing due_date — or a count the field would reject
    // (<= 0) — falls back to blank so the configured default applies.
    const loadedDueDays = daysBetweenIso(loadedOrderDate, order.due_date ? String(order.due_date).slice(0, 10) : '');
    setDueDays(loadedDueDays != null && loadedDueDays > 0 ? String(loadedDueDays) : '');
    // Pickup is standing state on the order too, and validateAdminOrderPayload
    // requires all three fields, so an unrestored pickup window would block the save.
    // pickup_time round-trips as the stored "Monday 09:00:00-12:00:00" string, which
    // is exactly what PickupScheduler matches its slots against.
    setPickupLocationId(order.pickup_location_id || '');
    setPickupDate(order.pickup_date ? String(order.pickup_date).slice(0, 10) : '');
    setPickupTime(order.pickup_time || '');
  }, []);

  useEffect(() => {
    if (!loadOngoingOrderId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getOngoingOrder(loadOngoingOrderId);
        if (cancelled) return;
        setItems(runningOrderLines(data.items, (_, idx) => localKey() + idx));
        if (data.order) {
          hydrateFromOrder(data.order);
          if (data.snapshot) {
            setCartDiscountPercent(Number(data.snapshot.cart_discount_percent ?? 0) || '');
          }
          setStep(2);
          setTab('items');
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [loadOngoingOrderId, hydrateFromOrder]);

  // ── Variant catalog for the items tab ───────────────────────────
  const variantLookup = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const productName = localizedField(p, 'name', lang) || p.product_code || '';
      for (const v of p.variants || []) {
        map.set(v.id, {
          ...v,
          productName,
          productCode: p.product_code,
          vat_rate: p.vat_rate != null ? Number(p.vat_rate) : (v.vat_rate != null ? Number(v.vat_rate) : 8.1),
        });
      }
    }
    return map;
  }, [products, lang]);

  // Active customer_prices keyed by variant — pre-fills the agreed price in the
  // items tab (mirrors backend applyCustomerPrices / getDiscountedPrice).
  const customerPriceByVariant = useMemo(() => {
    const map = new Map();
    for (const cp of customerPrices) {
      if (cp.variant_id) map.set(cp.variant_id, cp);
    }
    return map;
  }, [customerPrices]);

  const variantOptions = useMemo(
    () => Array.from(variantLookup.entries()).map(([value, v]) => {
      const agreed = effectiveCustomerPrice(v, customerPriceByVariant.get(value), 1);
      const displayPrice = agreed != null ? agreed : v.price;
      const totalStock = Number(v.stock_quantity || 0) + Number(v.reserved_stock || 0);
      const stockNote = Number(v.can_preorder) !== 0 || totalStock > 0
        ? ` — ${totalStock} in stock`
        : ' — out of stock';
      return {
        value,
        label: `${v.productName} — ${v.sku}${displayPrice != null ? ` (CHF ${Number(displayPrice).toFixed(2)}${agreed != null ? ' · agreed' : ''})` : ''}${stockNote}${Number(v.reserved_stock) > 0 ? ' · reserved' : ''}`,
        stock: totalStock,
      };
    }),
    [variantLookup, customerPriceByVariant]
  );

  const isPickup = fulfillmentMethod === 'pickup';
  // A line is submittable with any NON-ZERO whole quantity: negative means refund
  // (money back), positive means goods.
  const validItems = useMemo(
    () => items.filter((it) => {
      const qty = Number(it.quantity);
      if (!Number.isInteger(qty) || qty === 0) return false;
      if (it.type === 'adhoc') return Boolean((it.name || '').trim());
      return Boolean(it.variant_id);
    }),
    [items]
  );

  // ── Auto-applied offers ─────────────────────────────────────────
  // Evaluated server-side against the draft (active offers + volume discount).
  // Absent selection = apply everything applicable (the auto-apply default).
  const evaluateOffers = useCallback(async () => {
    if (validItems.length === 0) return;
    setOfferLoading(true);
    setOfferError(null);
    try {
      const res = await apiService.evaluateAdminOrderOffers(user.id, {
        items: validItems.map((it) => ({
          variant_id: it.type === 'adhoc' ? null : (it.variant_id || null),
          sku: it.type === 'adhoc' ? null : (it.sku || null),
          name: (it.name || '').trim(),
          quantity: Number(it.quantity) || 1,
          // Product quantities are always pieces of packs; the weight entry
          // mode is UI-only. Adhoc lines keep their chosen unit as metadata.
          unit: it.type === 'adhoc' ? (it.unit || 'pcs') : 'pcs',
          unit_price: Number(it.unit_price) || 0,
          discount_percent: Number(it.discount_percent) || 0,
          vat_rate: Number(it.vat_rate) || 0,
        })),
        fulfillment_method: fulfillmentMethod,
      });
      setOfferResult(res);
      const offered = res.offers.map((o) => String(o.id));
      const previousOffered = prevOfferIdsRef.current;
      setSelectedOfferIds((prev) => {
        if (prev == null || previousOffered == null) return offered;
        const kept = prev.map(String).filter((id) => offered.includes(id));
        // Auto-add offers that are newly applicable — EXCEPT offers the admin
        // explicitly deselected. An offer that dropped out of the applicable
        // set (trigger qty / subtotal dipped) and came back must stay off.
        const newlyApplicable = offered.filter(
          (id) => !previousOffered.has(id) && !deselectedOfferIdsRef.current.has(id)
        );
        return Array.from(new Set([...kept, ...newlyApplicable]));
      });
      prevOfferIdsRef.current = new Set(offered);
      setApplyVolumeDiscount((prev) => {
        if (Number(res.volumeDiscount) <= 0) return false;
        return prev == null ? true : prev;
      });
    } catch (err) {
      setOfferError(err.message);
    } finally {
      setOfferLoading(false);
    }
  }, [validItems, fulfillmentMethod, user.id]);

  useEffect(() => {
    if (validItems.length === 0) return undefined;
    const timer = setTimeout(() => { evaluateOffers(); }, 400);
    return () => clearTimeout(timer);
  }, [validItems, evaluateOffers]);

  // The admin-confirmed subset of the evaluated offers, for the live totals.
  const selectedOffers = useMemo(() => {
    if (!offerResult) return null;
    const want = new Set(
      selectedOfferIds == null
        ? offerResult.offers.map((o) => String(o.id))
        : selectedOfferIds.map(String)
    );
    const offers = offerResult.offers.filter((o) => want.has(String(o.id)));
    const freeKey = (f) => `${f.variant_id}|${f.reasonKey}`;
    const selectedFreeKeys = new Set(offers.flatMap((o) => (o.free_items || []).map(freeKey)));
    return {
      ruleDiscount: offers.reduce((s, o) => s + (Number(o.discount) || 0), 0),
      shippingFree: offers.some((o) => o.shipping_free),
      freeItems: (offerResult.freeItems || []).filter((fi) => selectedFreeKeys.has(freeKey(fi))),
      volumeDiscount: applyVolumeDiscount === false ? 0 : (Number(offerResult.volumeDiscount) || 0),
      volumeDiscountPercentage: applyVolumeDiscount === false ? null : (offerResult.volumeDiscountPercentage ?? null),
    };
  }, [offerResult, selectedOfferIds, applyVolumeDiscount]);

  // ── Live totals (client mirror — server recomputes authoritatively) ──
  const isBusiness = user.user_type === 'business';
  const accountBalance = Number(user.account_balance) || 0;
  const cartDiscount = Math.min(100, Math.max(0, Number(cartDiscountPercent) || 0));
  const totals = useMemo(() => {
    const provisional = computeAdminOrderTotals(items, 0, isPickup, config.vatRates, selectedOffers, accountBalance, isBusiness, cartDiscount);
    const autoShipping = getShippingCostForSubtotal(provisional.discountedSubtotal, config.shippingTiers, fulfillmentMethod);
    const hasOverride = !isPickup && shippingOverride !== '' && !Number.isNaN(Number(shippingOverride)) && Number(shippingOverride) >= 0;
    const shippingCost = hasOverride ? Number(shippingOverride) : autoShipping;
    return { ...computeAdminOrderTotals(items, shippingCost, isPickup, config.vatRates, selectedOffers, accountBalance, isBusiness, cartDiscount), autoShippingCost: autoShipping };
  }, [items, isPickup, fulfillmentMethod, shippingOverride, cartDiscount, config.vatRates, config.shippingTiers, selectedOffers, accountBalance, isBusiness]);

  const buildPayload = useCallback(() => ({
    items: validItems.map((it) => ({
      variant_id: it.type === 'adhoc' ? null : (it.variant_id || null),
      sku: it.type === 'adhoc' ? null : (it.sku || null),
      name: (it.name || '').trim(),
      quantity: Number(it.quantity) || 1,
      // Product quantities are canonical pieces of packs — the weight entry
      // mode never reaches the DB. Adhoc lines keep their chosen unit.
      unit: it.type === 'adhoc' ? (it.unit || 'pcs') : 'pcs',
      unit_price: Number(it.unit_price) || 0,
      discount_percent: Number(it.discount_percent) || 0,
      vat_rate: Number(it.vat_rate) || 0,
      // Identity of the persisted running-order line this row was loaded from, so
      // an edit updates that line instead of being merged by variant/price guesswork.
      order_item_id: it.order_item_id || null,
    })),
    payment_method: paymentMethod,
    fulfillment_method: fulfillmentMethod,
    shipping_cost: isPickup ? null : (shippingOverride === '' ? null : Number(shippingOverride)),
    shipping_address_id: destinationShippingId,
    billing_address_id: billingAddressId || null,
    pickup_location_id: isPickup ? (pickupLocationId || null) : null,
    pickup_time: isPickup ? pickupTime : null,
    pickup_date: isPickup ? (pickupDate || null) : null,
    description,
    order_date: orderDate,
    due_days: dueDays === '' || dueDays == null ? null : Number(dueDays),
    selected_offer_ids: selectedOfferIds == null ? null : selectedOfferIds.map(String),
    apply_volume_discount: applyVolumeDiscount,
    // null (an empty field) keeps the running order's standing value on append and
    // means "no discount" on a new order.
    cart_discount_percent: cartDiscountPercent === '' || cartDiscountPercent == null ? null : cartDiscount,
  }), [validItems, paymentMethod, fulfillmentMethod, destinationShippingId, isPickup, shippingOverride, billingAddressId, pickupLocationId, pickupTime, pickupDate, description, orderDate, dueDays, selectedOfferIds, applyVolumeDiscount, cartDiscountPercent, cartDiscount]);

  // ── Tab navigation ──────────────────────────────────────────────
  const handleTabChange = useCallback((nextTab) => {
    setTab(nextTab);
  }, []);

  // ── Step validation ─────────────────────────────────────────────
  const validateTab = useCallback((whichTab) => {
    const msgs = [];
    if (whichTab === 'items') {
      if (validItems.length === 0) msgs.push(t('takeOrderErrorNoItems', 'Add at least one product or custom line.'));
      if (items.some(it => !Number.isInteger(Number(it.quantity)) || Number(it.quantity) === 0)) {
        msgs.push(t('takeOrderErrorQtyWhole', 'Every line needs a whole quantity of at least 1, or a negative one for a refund.'));
      }
      if (totals.creditsExceedTotal) {
        msgs.push(t('takeOrderErrorRefundsExceedTotal', 'The refunds total more than the order. Reduce the refund or the discount on the whole order.'));
      }
      // Refunds are negative lines against goods already on the order, so a batch of
      // nothing but refunds only works when appending to a running monthly order.
      if (validItems.length > 0 && validItems.every(it => Number(it.quantity) < 0) && !ongoingMatch) {
        msgs.push(t('takeOrderErrorCreditOnly', 'An order needs at least one product line — a refund is a negative quantity on an order that has products.'));
      }
      if (cartDiscountPercent !== '' && cartDiscountPercent != null
          && (Number.isNaN(Number(cartDiscountPercent)) || Number(cartDiscountPercent) < 0 || Number(cartDiscountPercent) > 100)) {
        msgs.push(t('takeOrderErrorCartDiscount', 'The discount on the whole order must be between 0 and 100 %.'));
      }
      if (items.some(it => Number(it.unit_price) < 0)) {
        msgs.push(t('takeOrderErrorNegativePrice', 'A unit price cannot be negative — enter money back as a refund with a negative quantity.'));
      }
      for (const it of items) {
        if (it.type === 'adhoc') {
          if (!(it.name || '').trim()) msgs.push(t('takeOrderErrorAdhocName', 'Every custom line needs a description.'));
        } else if (!it.variant_id) {
          msgs.push(t('takeOrderErrorNoProduct', 'Every product line needs a product selected.'));
        }
      }
    } else if (whichTab === 'payment') {
      if (!paymentMethod) msgs.push(t('takeOrderErrorPaymentMethod', 'Choose a payment method.'));
      const n = Number(dueDays);
      if (dueDays === '' || dueDays == null || Number.isNaN(n) || n <= 0) {
        msgs.push(t('takeOrderErrorDueDays', 'Enter a positive number of days until payment is due.'));
      }
    } else if (whichTab === 'address') {
      if (isPickup) {
        if (!pickupLocationId) msgs.push(t('takeOrderErrorPickupLocation', 'Select a pickup location.'));
        if (!pickupTime) msgs.push(t('takeOrderErrorPickupTime', 'Select a pickup day / time window.'));
        if (!pickupDate) msgs.push(t('takeOrderErrorPickupDate', 'Select a pickup date.'));
      } else if (addresses.length > 0 && !shippingAddressId) {
        msgs.push(t('takeOrderErrorShippingAddress', 'Select a shipping address.'));
      }
      if (billingAddresses.length === 0) {
        msgs.push(t('takeOrderNoBillingAddresses', 'This customer has no saved billing address. Add one before placing the order.'));
      } else if (!billingAddressId) {
        msgs.push(t('takeOrderErrorBillingAddress', 'Select a billing address.'));
      }
      if (!isPickup && shippingOverride !== '' && (Number.isNaN(Number(shippingOverride)) || Number(shippingOverride) < 0)) {
        msgs.push(t('takeOrderErrorShippingCost', 'Shipping cost must be a non-negative number.'));
      }
    }
    return msgs;
  }, [validItems.length, validItems, items, paymentMethod, dueDays, isPickup, pickupLocationId, pickupTime, pickupDate, addresses.length, billingAddresses.length, shippingAddressId, billingAddressId, shippingOverride, cartDiscountPercent, totals, ongoingMatch, t]);

  const nextTab = () => {
    const errorsList = validateTab(tab);
    if (errorsList.length > 0) {
      setError(errorsList.join(' '));
      return;
    }
    setError(null);
    const idx = TABS.indexOf(tab);
    if (idx >= 0 && idx < TABS.length - 1) handleTabChange(TABS[idx + 1]);
  };

  const handleSubmit = async () => {
    let allErrors = [];
    for (const tb of TABS) allErrors = allErrors.concat(validateTab(tb));
    if (allErrors.length > 0) {
      setError(allErrors.join(' '));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await apiService.createAdminOrder(user.id, buildPayload());
      onSuccess();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const gotoStep2 = () => {
    if (!orderDate) {
      setError(t('takeOrderErrorOrderDate', 'An order date is required.'));
      return;
    }
    setError(null);
    setStep(2);
  };

  const stepChips = (
    <div className="take-order-steps" aria-label={t('takeOrderWizardSteps', 'Order wizard steps')}>
      <button
        type="button"
        className="take-order-step"
        aria-current={step === 1 ? 'step' : undefined}
        onClick={() => { setError(null); setStep(1); }}
      >
        <span className="take-order-step-number" aria-hidden="true">1</span>
        {t('takeOrderStep1', 'Order details')}
      </button>
      <button
        type="button"
        className="take-order-step"
        aria-current={step === 2 ? 'step' : undefined}
        onClick={() => (step === 1 ? gotoStep2() : null)}
      >
        <span className="take-order-step-number" aria-hidden="true">2</span>
        {t('takeOrderStep2', 'Items, payment & delivery')}
      </button>
    </div>
  );

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconUserCircle size={20} stroke={1.5} aria-hidden="true" />
          <Text fw={700} size="lg">
            {t('createOrderFor', 'Create Order for')} {user.name}
          </Text>
        </Group>
      }
      size="80rem"
      centered
      radius="md"
    >
      <Box pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ blur: 2 }} />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} aria-hidden="true" />}
            title={t('error', 'Error')}
            color="red"
            mb="md"
            withCloseButton
            onClose={() => setError(null)}
            role="alert"
          >
            {error}
          </Alert>
        )}

        {stepChips}

        {step === 1 && (
          <Stack gap="md" className="take-order-step1">
            <Alert icon={<IconInfoCircle size={16} aria-hidden="true" />} color="teal" variant="light">
              <Text size="sm">
                {t('takeOrderIntro', 'Create an order for this customer. Invoice or monthly payment only — card, TWINT and Apple/Google Pay are not available in this flow.')}
              </Text>
            </Alert>

            <TextInput
              label={t('takeOrderDescription', 'Order description (optional)')}
              description={t('takeOrderDescriptionHint', 'Shown to the customer on their order and in emails.')}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />

            <TextInput
              type="date"
              label={t('takeOrderOrderDate', 'Order date')}
              value={orderDate}
              onChange={(e) => setOrderDate(e.currentTarget.value)}
              required
              aria-required="true"
              styles={{ input: { maxWidth: 260 } }}
            />

            <Group justify="space-between" mt="md">
              <Group gap="xs">
                <Button variant="subtle" color="gray" onClick={onClose}>
                  {t('cancel', 'Cancel')}
                </Button>
                                <Button variant="light" leftSection={<IconRepeat size={16} aria-hidden="true" />} onClick={async () => {
                  if (!billingAddressId) {
                    setError(t('takeOrderErrorBillingAddress', 'Select a billing address.'));
                    setStep(2); setTab('address');
                    return;
                  }
                  if (!isPickup && addresses.length > 0 && !shippingAddressId) {
                    setError(t('takeOrderErrorShippingAddress', 'Select a shipping address.'));
                    setStep(2); setTab('address');
                    return;
                  }
                  // Match on billing address AND destination, the same key the server
                  // uses. With one running order per destination, a customer who ships
                  // to two addresses has two of them, and this shortcut loads the one
                  // for the destination currently selected rather than whichever came
                  // first.
                  const match = matchOngoingOrder(ongoingOrders, {
                    billingAddressId,
                    shippingAddressId: destinationShippingId,
                  });
                  if (match) {
                    try {
                      setLoading(true);
                      const data = await apiService.getOngoingOrder(match.id);
                      setItems(runningOrderLines(data.items, (_, idx) => localKey() + idx));
                      if (data.order) {
                        hydrateFromOrder(data.order);
                        if (data.snapshot) setCartDiscountPercent(Number(data.snapshot.cart_discount_percent ?? 0) || '');
                      }
                      setEditingRunningOrder(true); setStep(2); setTab('items');
                    } catch (e) { setError(e.message); } finally { setLoading(false); }
                    return;
                  }
                  // No open running order for this destination yet: the batch seeds
                  // one, which the server decides from the same key. Jump to the items
                  // step so the admin can keep working; the notice below tells them
                  // which running order this batch belongs to.
                  setPaymentMethod('monthly');
                  setStep(2); setTab('items');
                }}>
                  {t('takeOrderAddToRunning', 'Add to Running Order')}
                </Button>
              </Group>
              <Button rightSection={<IconArrowRight size={16} aria-hidden="true" />} onClick={gotoStep2}>
                {t('takeOrderNext', 'Next')}
              </Button>
            </Group>
          </Stack>
        )}

        {step === 2 && (
          <>
            {runningOrderNotice && (
              <Alert
                icon={<IconRepeat size={16} aria-hidden="true" />}
                color={ongoingMatch ? 'teal' : 'blue'}
                variant="light"
                mb="md"
                role="status"
              >
                {runningOrderNotice}
              </Alert>
            )}
            <Tabs value={tab} onChange={handleTabChange} className="take-order-tabs">
              <Tabs.List>
              <Tabs.Tab value="items" leftSection={<IconPackage size={16} aria-hidden="true" />}>
                {t('takeOrderTabItems', 'Items')}
              </Tabs.Tab>
              <Tabs.Tab value="payment">{t('takeOrderTabPayment', 'Payment')}</Tabs.Tab>
              <Tabs.Tab value="address">{t('takeOrderTabAddress', 'Address & Fulfillment')}</Tabs.Tab>
            </Tabs.List>

            <div className="take-order-grid">
              <div className="take-order-grid__main">
                  <Tabs.Panel value="items" pt="md">
                  <OrderItemsTab
                    items={items}
                    onChange={setItems}
                    variantOptions={variantOptions}
                    variantLookup={variantLookup}
                    customerPricesByVariant={customerPriceByVariant}
                    units={config.units}
                    vatRates={config.vatRates}
                    cartDiscountPercent={cartDiscountPercent}
                    onCartDiscountPercentChange={setCartDiscountPercent}
                    t={t}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="payment" pt="md">
                  <OrderPaymentTab disabled={editingRunningOrder}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={editingRunningOrder ? () => {} : setPaymentMethod}
                    monthlyInvoicingEnabled={Boolean(user.monthly_invoicing_enabled)}
                    dueDays={dueDays}
                    setDueDays={setDueDays}
                    defaultDueDateDays={config.defaultDueDateDays}
                    t={t}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="address" pt="md">
                  <OrderAddressTab
                    fulfillmentMethod={fulfillmentMethod}
                    setFulfillmentMethod={setFulfillmentMethod}
                    addresses={addresses}
                    shippingAddressId={shippingAddressId}
                    setShippingAddressId={setShippingAddressId}
                    billingAddresses={billingAddresses}
                    billingAddressId={billingAddressId}
                    setBillingAddressId={setBillingAddressId}
                    locations={locations}
                    holidays={holidays}
                    pickupLocationId={pickupLocationId}
                    setPickupLocationId={setPickupLocationId}
                    pickupTime={pickupTime}
                    setPickupTime={setPickupTime}
                    pickupDate={pickupDate}
                    setPickupDate={setPickupDate}
                    shippingOverride={shippingOverride}
                    setShippingOverride={setShippingOverride}
                    autoShippingCost={totals.autoShippingCost}
                    t={t}
                  />
                </Tabs.Panel>
              </div>
              <aside className="take-order-grid__aside">
                <CustomerPricingPanel
                  loading={loading}
                  items={items}
                  variantLookup={variantLookup}
                  customerPricesByVariant={customerPriceByVariant}
                  t={t}
                />
                <OffersPanel
                  loading={offerLoading}
                  error={offerError}
                  result={offerResult}
                  selectedOfferIds={selectedOfferIds}
                  applyVolumeDiscount={applyVolumeDiscount}
                  onToggleOffer={(id) => setSelectedOfferIds((prev) => {
                    const cur = new Set(
                      prev == null
                        ? (offerResult?.offers || []).map((o) => String(o.id))
                        : prev.map(String)
                    );
                    if (cur.has(id)) {
                      cur.delete(id);
                      deselectedOfferIdsRef.current.add(id);
                    } else {
                      cur.add(id);
                      deselectedOfferIdsRef.current.delete(id);
                    }
                    return Array.from(cur);
                  })}
                  onToggleVolume={() => setApplyVolumeDiscount((prev) => !prev)}
                  onRefresh={evaluateOffers}
                  t={t}
                />
                <OrderTotalsSummary totals={totals} t={t} />
              </aside>
            </div>

            <div className="take-order-actions">
              <Tooltip label={t('takeOrderBackToDetails', 'Back to order details')}>
                <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={16} aria-hidden="true" />} onClick={() => setStep(1)}>
                  {t('takeOrderBack', 'Back')}
                </Button>
              </Tooltip>
              <div className="take-order-actions__right">
                {tab !== TABS[TABS.length - 1] ? (
                  <Button rightSection={<IconArrowRight size={16} aria-hidden="true" />} onClick={nextTab}>
                    {t('takeOrderNext', 'Next')}
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} loading={submitting} data-autofocus>
                    {submitting ? t('creating', 'Creating...') : t('takeOrderPlaceOrder', 'Place Order')}
                  </Button>
                )}
              </div>
            </div>
            </Tabs>
          </>
        )}
      </Box>
    </Modal>
  );
}
